import React, { useCallback, useEffect, useState } from "react";
import { getISOWeek } from "date-fns";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { ExternalLink, Search, Trash2 } from "lucide-react";
import { Badge, Button, Card } from "../components/legacy-ui.js";
import { AppPageHeader, AppTable } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { formatCurrency, formatDate } from "../utils/formatters.js";
import {
  deleteMilkProviderPayment,
  getMilkProviderPayments,
  getProviderList,
} from "../utils/api.js";
import { getMilkWeekStartKey } from "../utils/milkUtils.js";
import { resolveBankImage, resolveBankLabel } from "../utils/venezuelanBanks.js";
import { LoadingState } from "../components/motion.js";

const formatDay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-VE", {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatWeek = (start, end) => {
  if (!start && !end) return "—";
  return `${formatDay(start)} – ${formatDay(end)}`;
};

const weekNumberOf = (start, end) => {
  const key = String(end || start || "").slice(0, 10);
  if (key.length < 10) return "";
  return getISOWeek(new Date(`${key}T12:00:00`));
};

const ProviderPaymentsModule = () => {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [byProvider, setByProvider] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerId, setProviderId] = useState("");
  const [from, setFrom] = useState(() => getMilkWeekStartKey());
  const [to, setTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totals, setTotals] = useState({ amountInUSD: 0, amountInBS: 0, count: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [providerId, from, to, debouncedSearch]);

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const res = await getProviderList();
        setProviders(res.data.data || []);
      } catch (error) {
        console.error("Error loading providers", error);
      }
    };
    loadProviders();
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMilkProviderPayments({
        provider: providerId || undefined,
        from: from || undefined,
        to: to || undefined,
        search: debouncedSearch || undefined,
        page,
        limit: 40,
      });
      setRows(res.data.data || []);
      setByProvider(res.data.byProvider || []);
      setTotals(res.data.totals || { amountInUSD: 0, amountInBS: 0, count: 0 });
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      toast.error(
        error.response?.data?.error || "No se pudo cargar el historial de pagos",
        { position: "top-center" },
      );
    } finally {
      setLoading(false);
    }
  }, [providerId, from, to, debouncedSearch, page]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDelete = async (payment) => {
    const name = payment.provider?.name || "este proveedor";
    if (!window.confirm(`¿Eliminar el pago de ${name} por ${formatCurrency(payment.amountInUSD)}?`)) {
      return;
    }
    try {
      await deleteMilkProviderPayment(payment._id);
      toast.success("Pago eliminado", { position: "top-center" });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar el pago", {
        position: "top-center",
      });
    }
  };

  const selectedProvider = providers.find((item) => item._id === providerId);
  const methodName = (payment) =>
    payment.paymentMethod?.name || payment.method || "—";
  const bankInfo = (payment) => {
    const label = resolveBankLabel(payment.bank);
    if (!label) return null;
    return {
      label,
      image: resolveBankImage(payment.bank),
    };
  };
  const emitLabel = (payment) => {
    if (!payment?.emitFrom) return "";
    if (payment.emitFrom === "juridico") {
      return payment.emitSource?.name
        ? `Jurídico · ${payment.emitSource.name}`
        : "Jurídico";
    }
    if (payment.emitFrom === "otra") {
      return payment.emitSource?.name
        ? `Otra · ${payment.emitSource.name}`
        : "Otra";
    }
    if (payment.emitFrom === "personal") return "Personal";
    return payment.emitFrom;
  };

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Movimientos de pago")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Movimientos de pago"
            subtitle="Historial de pagos registrados a proveedores de leche"
            actions={
              <Button variant="secondary" onClick={fetchPayments}>
                Actualizar
              </Button>
            }
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
            <label className="text-xs text-gray-500">
              Proveedor
              <select
                className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-800"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
              >
                <option value="">Todos los proveedores</option>
                {providers.map((provider) => (
                  <option key={provider._id} value={provider._id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-500">
              Desde (jueves)
              <input
                type="date"
                className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-800"
                value={from}
                onChange={(e) =>
                  setFrom(e.target.value ? getMilkWeekStartKey(e.target.value) : "")
                }
              />
            </label>
            <label className="text-xs text-gray-500">
              Hasta
              <input
                type="date"
                className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-800"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <label className="text-xs text-gray-500">
              Buscar
              <div className="relative mt-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Referencia o nota…"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </label>
          </div>

          {byProvider.length > 0 && (
            <div className="hidden md:flex flex-wrap gap-1.5 mt-3">
              <button
                type="button"
                onClick={() => setProviderId("")}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  !providerId
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"
                }`}
              >
                Todos
              </button>
              {byProvider.map((item) => (
                <button
                  type="button"
                  key={String(item.providerId)}
                  onClick={() => setProviderId(String(item.providerId))}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                    providerId === String(item.providerId)
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"
                  }`}
                >
                  {item.name} ({item.count})
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 gap-2 max-w-3xl">
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0 min-[420px]:col-span-2 sm:col-span-1">
              <p className="text-[11px] text-gray-500">Pagos</p>
              <p className="text-xl font-bold text-gray-900 tabular-nums">{totals.count || 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0">
              <p className="text-[11px] text-gray-500 truncate">
                Total USD
                {selectedProvider ? (
                  <span className="hidden sm:inline"> · {selectedProvider.name}</span>
                ) : null}
              </p>
              <p className="text-base sm:text-lg font-bold text-indigo-700 tabular-nums leading-tight break-words">
                {formatCurrency(totals.amountInUSD)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0">
              <p className="text-[11px] text-gray-500">Total BS</p>
              <p className="text-base sm:text-lg font-bold text-emerald-700 tabular-nums leading-tight break-words">
                {formatCurrency(totals.amountInBS, "BS")}
              </p>
            </div>
          </div>
        </div>

        <div className="app-content">
          {loading ? (
            <LoadingState label="Cargando pagos" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-12">
              No hay pagos registrados con esos filtros.
            </p>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {rows.map((payment) => {
                  const bank = bankInfo(payment);
                  return (
                  <Card key={payment._id} className="p-4 space-y-2">
                    <div className="flex justify-between gap-2 items-start">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {payment.provider?.name || "Proveedor"}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {formatDate(payment.paymentDate || payment.createdAt)}
                        </p>
                      </div>
                      <Badge type="success">{formatCurrency(payment.amountInUSD)}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      {formatCurrency(payment.amount, payment.currency)} · {methodName(payment)}
                    </p>
                    {bank ? (
                      <p className="flex items-center gap-1.5 text-xs text-gray-600">
                        {bank.image ? (
                          <img
                            src={bank.image}
                            alt=""
                            className="h-4 w-4 rounded object-contain"
                          />
                        ) : null}
                        {bank.label}
                        {emitLabel(payment) ? ` · ${emitLabel(payment)}` : ""}
                      </p>
                    ) : emitLabel(payment) ? (
                      <p className="text-xs text-gray-600">{emitLabel(payment)}</p>
                    ) : null}
                    <p className="text-xs text-gray-500">
                      N° {weekNumberOf(payment.weekStart, payment.weekEnd) || "—"}
                      {" · "}
                      {formatWeek(payment.weekStart, payment.weekEnd)}
                      {payment.reference ? ` · Ref. ${payment.reference}` : ""}
                    </p>
                    <div className="flex items-center justify-between pt-1">
                      {payment.receiptUrl ? (
                        <a
                          href={payment.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-indigo-600"
                        >
                          <ExternalLink size={14} /> Comprobante
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">Sin comprobante</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(payment)}
                        className="text-red-400 hover:text-red-600 p-1"
                        aria-label="Eliminar pago"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </Card>
                  );
                })}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "date", title: "Fecha y hora" },
                    { key: "provider", title: "Proveedor" },
                    { key: "weekNo", title: "N° Semana" },
                    { key: "week", title: "Semana" },
                    { key: "method", title: "Método" },
                    { key: "bank", title: "Banco" },
                    { key: "emit", title: "Emitido" },
                    { key: "amount", title: "Monto", align: "right" },
                    { key: "usd", title: "USD", align: "right" },
                    { key: "ref", title: "Referencia" },
                    { key: "actions", title: "", align: "right" },
                  ]}
                >
                  {rows.map((payment) => {
                    const bank = bankInfo(payment);
                    return (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        {formatDate(payment.paymentDate || payment.createdAt)}
                      </td>
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {payment.provider?.name || "Proveedor"}
                      </td>
                      <td className="px-6 py-3.5 text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {weekNumberOf(payment.weekStart, payment.weekEnd) || "—"}
                      </td>
                      <td className="px-6 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        {formatWeek(payment.weekStart, payment.weekEnd)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-700">
                        {methodName(payment)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-700">
                        {bank ? (
                          <span className="inline-flex items-center gap-1.5">
                            {bank.image ? (
                              <img
                                src={bank.image}
                                alt=""
                                className="h-5 w-5 rounded object-contain"
                              />
                            ) : null}
                            <span className="truncate max-w-[140px]">
                              {bank.label}
                            </span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-700">
                        <span className="truncate max-w-[160px] block">
                          {emitLabel(payment) || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right text-gray-700">
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right font-semibold text-gray-900">
                        {formatCurrency(payment.amountInUSD)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {payment.reference || "—"}
                        {payment.notes ? (
                          <span className="block text-xs text-gray-400">{payment.notes}</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <div className="inline-flex items-center gap-2">
                          {payment.receiptUrl ? (
                            <a
                              href={payment.receiptUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-indigo-600 hover:text-indigo-800"
                              title="Ver comprobante"
                            >
                              <ExternalLink size={16} />
                            </a>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleDelete(payment)}
                            className="text-red-400 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </AppTable>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-500">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProviderPaymentsModule;
